<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AgentMessage from './AgentMessage.vue'
import AgentInput from './AgentInput.vue'
import { useAgentStore } from '@/stores/agent'
import { icons } from '@/assets/icons'

const agent = useAgentStore()
const store = useStore()

const listRef = ref(null)
const panelRef = ref(null)
const showHistory = ref(false)
const activeMenuSessionId = ref('')
const renameDialogOpen = ref(false)
const renameTitleInput = ref('')
const renameTargetSessionId = ref('')
const renameSaving = ref(false)
const deleteDialogOpen = ref(false)
const deleteTargetSessionId = ref('')
const deleteSaving = ref(false)

function onOutsideClick(e) {
  if (!panelRef.value) return
  if (!document.contains(e.target)) return
  if (renameDialogOpen.value) {
    const target = e.target
    if (target instanceof Element && target.closest('.agent-confirm-dialog')) {
      return
    }
  }
  if (deleteDialogOpen.value) {
    const target = e.target
    if (target instanceof Element && target.closest('.agent-delete-dialog')) {
      return
    }
  }
  if (panelRef.value.contains(e.target)) {
    const target = e.target
    if (
      activeMenuSessionId.value &&
      !(target instanceof Element && target.closest('.history__menu-wrap'))
    ) {
      activeMenuSessionId.value = ''
    }
    return
  }
  activeMenuSessionId.value = ''
  agent.toggle()
}

onMounted(() => {
  buildMixedSuggestions()
  nextTick(() => document.addEventListener('mousedown', onOutsideClick))
})
onUnmounted(() => {
  document.removeEventListener('mousedown', onOutsideClick)
})

const greeting = computed(() => {
  const h = new Date().getHours()
  if (h < 6) return '凌晨好'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
})

const displayName = computed(
  () =>
    store.state.user?.full_name?.trim() ||
    store.state.user?.nickname ||
    store.state.user?.username ||
    '同学',
)

const isWelcome = computed(() => !agent.messages.value.some((m) => m.role === 'user'))

const suggestionPool = [
  [
    { tag: '能力介绍', icon: '✨', text: '作为视频问答入口，你能帮我做哪些事？需要我先上传视频吗？' },
    { tag: '快速上手', icon: '🚀', text: '第一次用 SenseMate，想先做视频问答，建议我按什么顺序体验各功能？' },
    { tag: '视频要点', icon: '📝', text: '我上传教学视频后，请用「结论—关键点—可执行建议」结构做摘要。' },
    { tag: '时间追问', icon: '⏱️', text: '分析视频时，怎样提问才能让你准确对应到某一分钟的内容？' },
  ],
  [
    { tag: '图片识别', icon: '🖼️', text: '上传图片后你能识别哪些信息？请给我一个示例输出。' },
    { tag: '视频理解', icon: '🎬', text: '我想分析一个教学视频，应该给你什么提示词效果更好？' },
    { tag: '视频摘要', icon: '📚', text: '请给我一个视频摘要模板：结论、关键点、行动建议。' },
    { tag: '视频分类', icon: '🏷️', text: '帮我设计一个短视频自动分类标签体系（5-8类）。' },
  ],
  [
    { tag: '智能剪辑', icon: '✂️', text: '帮我定义“精彩片段”的判定规则，用于自动剪辑。' },
    { tag: '视频生成', icon: '🎥', text: '我输入一段脚本后，怎么拆分成可生成的视频分镜提示词？' },
    { tag: '报告输出', icon: '📊', text: '请把分析结果整理成可直接汇报的结构化大纲。' },
    { tag: '提示词优化', icon: '🧠', text: '给我 5 条通用提示词，提升图片/视频分析的准确度。' },
  ],
]

const suggestionCount = 4
const suggestions = ref([])

function randomPickFromArray(list) {
  if (!Array.isArray(list) || !list.length) return null
  const idx = Math.floor(Math.random() * list.length)
  return list[idx]
}

function buildMixedSuggestions() {
  const groups = Array.isArray(suggestionPool)
    ? suggestionPool.filter((g) => Array.isArray(g) && g.length)
    : []
  if (!groups.length) {
    suggestions.value = []
    return
  }

  const mixed = groups.map((group) => randomPickFromArray(group)).filter(Boolean)
  const allItems = groups.flat()
  while (mixed.length < suggestionCount && allItems.length) {
    const picked = randomPickFromArray(allItems)
    if (!picked) break
    const exists = mixed.some((item) => item.text === picked.text)
    if (!exists) {
      mixed.push(picked)
    } else if (mixed.length >= allItems.length) {
      break
    }
  }

  suggestions.value = [...mixed].sort(() => Math.random() - 0.5).slice(0, suggestionCount)
}

function shuffleSuggestions() {
  buildMixedSuggestions()
}

function sendSuggestion(item) {
  agent.send(item.text)
}

const groupedSessionHistory = computed(() => {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const sevenDaysAgo = startOfToday - 7 * 24 * 60 * 60 * 1000
  const groups = { today: [], recent: [], older: [] }
  for (const item of agent.sessionHistory.value || []) {
    const ts = Number(item?.updated_at || 0) * 1000
    if (ts >= startOfToday) {
      groups.today.push(item)
    } else if (ts >= sevenDaysAgo) {
      groups.recent.push(item)
    } else {
      groups.older.push(item)
    }
  }
  return [
    { key: 'today', title: '今天', items: groups.today },
    { key: 'recent', title: '近7天', items: groups.recent },
    { key: 'older', title: '更早', items: groups.older },
  ].filter((g) => g.items.length > 0)
})

const showTyping = computed(() => {
  if (!agent.sending.value) return false
  const last = agent.messages.value[agent.messages.value.length - 1]
  if (!last || last.role !== 'assistant') return true
  return !String(last.content || '').trim()
})

async function toggleHistory() {
  if (showHistory.value) {
    showHistory.value = false
    activeMenuSessionId.value = ''
    return
  }
  showHistory.value = true
  await agent.fetchSessionHistory()
}

async function openHistorySession(item) {
  if (!item?.session_id) return
  activeMenuSessionId.value = ''
  await agent.restoreSession(item.session_id)
  showHistory.value = false
  followStreamScroll.value = true
  await nextTick()
  scrollChatToBottom()
}

function toggleHistoryMenu(sessionId) {
  activeMenuSessionId.value = activeMenuSessionId.value === sessionId ? '' : sessionId
}

async function renameHistorySession(item) {
  activeMenuSessionId.value = ''
  if (!item?.session_id) return
  renameTargetSessionId.value = item.session_id
  renameTitleInput.value = (item?.title || '新会话').trim()
  renameDialogOpen.value = true
}

function closeRenameDialog(force = false) {
  if (renameSaving.value && !force) return
  renameDialogOpen.value = false
  renameTargetSessionId.value = ''
  renameTitleInput.value = ''
}

async function confirmRenameDialog() {
  const title = renameTitleInput.value.trim()
  if (!title || !renameTargetSessionId.value || renameSaving.value) return
  renameSaving.value = true
  try {
    await agent.renameHistorySession(renameTargetSessionId.value, title)
    closeRenameDialog(true)
  } finally {
    renameSaving.value = false
  }
}

function onRenameInputEnter(e) {
  if (e.key !== 'Enter') return
  e.preventDefault()
  confirmRenameDialog()
}

async function deleteHistorySession(item) {
  activeMenuSessionId.value = ''
  if (!item?.session_id) return
  deleteTargetSessionId.value = item.session_id
  deleteDialogOpen.value = true
}

function closeDeleteDialog(force = false) {
  if (deleteSaving.value && !force) return
  deleteDialogOpen.value = false
  deleteTargetSessionId.value = ''
}

async function confirmDeleteDialog() {
  if (!deleteTargetSessionId.value || deleteSaving.value) return
  deleteSaving.value = true
  try {
    await agent.deleteHistorySession(deleteTargetSessionId.value)
    closeDeleteDialog(true)
  } finally {
    deleteSaving.value = false
  }
}

/** 流式输出时是否自动滚到底：由用户滚动行为决定，不用「离底 80px」Guess，避免和手指/滚轮对抗 */
const followStreamScroll = ref(true)
let scrollProgrammaticGuard = 0

function distanceFromBottom(el) {
  return el.scrollHeight - el.scrollTop - el.clientHeight
}

function scrollChatToBottom() {
  const el = listRef.value
  if (!el) return
  scrollProgrammaticGuard++
  el.scrollTop = el.scrollHeight
  requestAnimationFrame(() => {
    scrollProgrammaticGuard--
  })
}

function onChatListScroll() {
  if (scrollProgrammaticGuard > 0) return
  const el = listRef.value
  if (!el) return
  const gap = distanceFromBottom(el)
  // 滞回：贴底则恢复跟随；明显离开底部才关闭，避免小幅抖动反复抢夺
  if (gap <= 6) followStreamScroll.value = true
  else if (gap >= 52) followStreamScroll.value = false
}

/** 仅在用户未主动上翻时，随流式内容增高而滚到底 */
function scrollStreamIfFollowing() {
  if (!followStreamScroll.value) return
  scrollChatToBottom()
}

watch(
  () => agent.messages.value.length,
  async () => {
    followStreamScroll.value = true
    await nextTick()
    scrollChatToBottom()
  },
)

watch(
  () => {
    const last = agent.messages.value[agent.messages.value.length - 1]
    return `${agent.sending.value}-${last?.role || ''}-${last?.content || ''}`
  },
  async () => {
    await nextTick()
    scrollStreamIfFollowing()
  },
)
</script>

<template>
  <aside class="agent-dialog" role="dialog" aria-label="视频问答" ref="panelRef">
    <header class="head">
      <div class="head__brand">
        <img :src="icons.agent" alt="助手" class="head__avatar" />
        <div class="head__name">SenseMate</div>
      </div>
      <div class="head__spacer"></div>
      <button
        class="icon-btn icon-btn--history"
        type="button"
        data-tip="历史记录"
        aria-label="历史记录"
        :aria-expanded="showHistory"
        @click="toggleHistory"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="8.2" />
          <path d="M12 7.6v4.8l3.1 2.2" />
        </svg>
      </button>
      <button class="icon-btn icon-btn--new-chat" type="button" data-tip="新建对话" aria-label="新建对话" @click="agent.reset">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="4" y="3.5" width="14.5" height="17" rx="3"></rect>
          <path d="M8 8.5h6.5M8 12h6.5M8 15.5h5"></path>
          <path d="M18.2 6.4l2.8 2.8M14.8 12.8l6.2-6.2"></path>
        </svg>
      </button>
      <button class="icon-btn icon-btn--close" type="button" data-tip="收起" aria-label="收起" @click="agent.toggle">×</button>
    </header>

    <div v-if="showHistory" class="history">
      <div v-if="agent.historyLoading.value" class="history__state">加载中...</div>
      <div v-else-if="!agent.sessionHistory.value.length" class="history__state">暂无历史会话</div>
      <div v-else class="history__groups">
        <section v-for="group in groupedSessionHistory" :key="group.key" class="history__group">
          <div class="history__group-title">{{ group.title }}</div>
          <div class="history__list">
            <div
              v-for="item in group.items"
              :key="item.session_id"
              class="history__item"
              :class="{ 'is-active': item.session_id === agent.sessionId.value }"
              @click="openHistorySession(item)"
            >
              <div class="history__main">
                <div class="history__title">{{ item.title || '新会话' }}</div>
              </div>
              <div class="history__menu-wrap">
                <button
                  class="history__menu-btn"
                  type="button"
                  aria-label="历史会话操作"
                  @click.stop="toggleHistoryMenu(item.session_id)"
                >
                  <span></span><span></span><span></span>
                </button>
                <div v-if="activeMenuSessionId === item.session_id" class="history__menu" @click.stop>
                  <button type="button" class="history__menu-item" @click="renameHistorySession(item)">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 20h4l9.8-9.8a1.6 1.6 0 0 0 0-2.3l-1.7-1.7a1.6 1.6 0 0 0-2.3 0L4 16v4z" />
                      <path d="m12.8 7.2 4 4" />
                    </svg>
                    <span>重命名</span>
                  </button>
                  <button
                    type="button"
                    class="history__menu-item history__menu-item--danger"
                    @click="deleteHistorySession(item)"
                  >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4.5 7h15" />
                      <path d="M9 7V5.6c0-.9.7-1.6 1.6-1.6h2.8c.9 0 1.6.7 1.6 1.6V7" />
                      <path d="M7.5 7l.8 11c.1 1.1 1 2 2.2 2h2.9c1.1 0 2.1-.9 2.2-2l.8-11" />
                      <path d="M10 11.2v5.8M14 11.2v5.8" />
                    </svg>
                    <span>删除</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div v-else-if="isWelcome" class="welcome">
      <div class="welcome__hero">
        <div class="welcome__text">
          <h1>{{ greeting }}，{{ displayName }}</h1>
          <p>
            我是「SenseMate」侧栏智能体：支持文字、配图与视频的多轮问答（解读、要点、时间线等），
            与全页「视频问答」入口会话独立、能力类似。分类、摘要、剪辑与生成仍在各功能页完成~
          </p>
        </div>
        <div class="welcome__hi">
          <div class="hi-bubble">hi</div>
          <img :src="icons.agent" alt="helper" />
        </div>
      </div>

      <div class="suggest__head">
        <span>你可以这样问我：</span>
        <button class="shuffle" @click="shuffleSuggestions">↻ 换一换</button>
      </div>
      <div class="suggest">
        <button
          v-for="(s, i) in suggestions"
          :key="i"
          class="suggest__item"
          @click="sendSuggestion(s)"
        >
          <span class="suggest__icon">{{ s.icon }}</span>
          <span class="suggest__tag">{{ s.tag }}</span>
          <span class="suggest__text">{{ s.text }}</span>
          <span class="suggest__arrow">›</span>
        </button>
      </div>
    </div>

    <div v-else class="list" ref="listRef" @scroll.passive="onChatListScroll">
      <AgentMessage v-for="(m, i) in agent.messages.value" :key="i" :message="m" />
      <div v-if="showTyping" class="typing">
        <img :src="icons.agent" alt="" class="typing__avatar" />
        <span class="typing__text">正在思考中</span>
        <span class="typing__dots" aria-hidden="true">
          <i></i><i></i><i></i>
        </span>
      </div>
    </div>

    <AgentInput
      v-if="!showHistory"
      :disabled="agent.sending.value"
      :suggestions="suggestions"
      @send="agent.send"
      @stop="agent.stop"
      @shuffle-suggestions="shuffleSuggestions"
    />

    <div class="foot">内容由 AI 大模型生成，仅供参考</div>
  </aside>
  <Teleport to="body">
    <div
      v-if="renameDialogOpen"
      class="agent-confirm-mask"
      role="presentation"
      @click.self="closeRenameDialog"
    >
      <div class="agent-confirm-dialog" role="dialog" aria-modal="true" aria-label="重命名会话">
        <p class="agent-confirm-text">编辑对话名称</p>
        <input
          v-model="renameTitleInput"
          type="text"
          class="agent-rename-input"
          maxlength="64"
          placeholder="请输入会话名称"
          @keydown="onRenameInputEnter"
        />
        <div class="agent-confirm-actions">
          <button
            type="button"
            class="agent-confirm-btn agent-confirm-btn--ghost"
            :disabled="renameSaving"
            @click="closeRenameDialog"
          >
            取消
          </button>
          <button
            type="button"
            class="agent-confirm-btn agent-confirm-btn--primary"
            :disabled="renameSaving || !renameTitleInput.trim()"
            @click="confirmRenameDialog"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  </Teleport>
  <Teleport to="body">
    <div
      v-if="deleteDialogOpen"
      class="agent-confirm-mask"
      role="presentation"
      @click.self="closeDeleteDialog"
    >
      <div class="agent-delete-dialog" role="dialog" aria-modal="true" aria-label="删除对话确认">
        <button type="button" class="agent-delete-close" aria-label="关闭" @click="closeDeleteDialog">×</button>
        <p class="agent-delete-title">确定删除对话？</p>
        <p class="agent-delete-text">删除后，聊天记录将不可恢复。</p>
        <div class="agent-confirm-actions">
          <button
            type="button"
            class="agent-confirm-btn agent-confirm-btn--ghost"
            :disabled="deleteSaving"
            @click="closeDeleteDialog"
          >
            取消
          </button>
          <button
            type="button"
            class="agent-confirm-btn agent-confirm-btn--danger"
            :disabled="deleteSaving"
            @click="confirmDeleteDialog"
          >
            删除
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.agent-dialog {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  width: 460px;
  height: 660px;
  max-height: calc(100vh - 80px);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 20px 44px rgba(17, 24, 39, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
  animation: pop 0.22s ease-out;
}
@keyframes pop {
  from {
    opacity: 0;
    transform: scale(0.88) translateY(16px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f6faff;
  border-bottom: 1px solid #eef2f8;
}
.head__brand {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.head__avatar {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
}
.head__name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2430;
}
.head__spacer {
  flex: 1;
}
.icon-btn {
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.icon-btn svg {
  width: 18px;
  height: 18px;
  display: block;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon-btn--history svg {
  width: 20px;
  height: 20px;
  stroke-width: 2.1;
}
.icon-btn--new-chat svg {
  width: 18px;
  height: 18px;
  stroke-width: 1.9;
}
.icon-btn[data-tip]:not([aria-expanded='true']):hover::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  padding: 6px 9px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 8;
}
.icon-btn[data-tip]:not([aria-expanded='true']):hover::before {
  content: '';
  position: absolute;
  left: 50%;
  top: calc(100% + 3px);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 5px solid #111827;
  pointer-events: none;
  z-index: 8;
}
.icon-btn:hover {
  background: #eef2ff;
  color: #3b82f6;
}
.icon-btn[aria-expanded='true'] {
  background: #e9f0ff;
  color: #3b82f6;
}
.icon-btn--close:hover {
  background: #ffe9e9;
  color: #ef4444;
}

.history {
  flex: 1;
  overflow: auto;
  padding: 16px 14px 10px;
  background: #fff;
}
.history__state {
  color: #9aa3b2;
  font-size: 13px;
  padding: 16px 6px;
}
.history__groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.history__group-title {
  font-size: 12px;
  color: #9aa3b2;
  margin: 2px 2px 8px;
}
.history__list {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.history__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px 4px;
  min-height: 30px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}
.history__item:hover {
  background: #fafbfc;
}
.history__item.is-active {
  background: #f3f4f6;
}
.history__main {
  min-width: 0;
  flex: 1;
}
.history__title {
  font-size: 14px;
  color: #1f2430;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.history__menu-wrap {
  position: relative;
  flex: 0 0 auto;
}
.history__menu-btn {
  width: 14px;
  height: 18px;
  border: none;
  border-radius: 6px;
  background: #f3f4f6;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0;
  opacity: 0;
  visibility: hidden;
  transition: background 0.15s, opacity 0.15s, visibility 0.15s;
}
.history__menu-btn span {
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: currentColor;
}
.history__menu-btn:hover {
  background: #e5e7eb;
  opacity: 1;
}
.history__item:hover .history__menu-btn,
.history__item:focus-within .history__menu-btn,
.history__menu-wrap:focus-within .history__menu-btn {
  opacity: 1;
  visibility: visible;
}
.history__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  width: max-content;
  min-width: 0;
  max-width: min(80vw, 180px);
  background: #fff;
  border: 1px solid #e9edf5;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.16);
  padding: 6px 5px;
  z-index: 6;
}
.history__menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #1f2430;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.2;
  text-align: left;
  padding: 8px 9px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.history__menu-item svg {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.history__menu-item:hover {
  background: #f7f8fa;
}
.history__menu-item--danger {
  color: #ef4444;
}

.welcome {
  flex: 1;
  overflow: auto;
  padding: 22px 22px 10px;
  background: #fff;
}
.welcome__hero {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 22px;
}
.welcome__text {
  flex: 1;
  min-width: 0;
}
.welcome__text h1 {
  font-size: 24px;
  font-weight: 700;
  color: #1f2430;
  margin: 0 0 10px;
}
.welcome__text p {
  font-size: 13px;
  color: #5b6473;
  line-height: 1.75;
  margin: 0;
}
.welcome__hi {
  position: relative;
  flex: 0 0 108px;
  width: 108px;
  height: 108px;
}
.welcome__hi img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 6px 14px rgba(43, 124, 255, 0.18));
}
.hi-bubble {
  position: absolute;
  top: -2px;
  left: -6px;
  background: #ff7a59;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 3px 10px 3px 10px;
  border-radius: 16px 16px 16px 4px;
  transform: rotate(-8deg);
  box-shadow: 0 4px 10px rgba(255, 122, 89, 0.35);
}

.suggest__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 10px;
}
.shuffle {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}
.shuffle:hover {
  background: #f3f4f6;
  color: #3b82f6;
}

.suggest {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.suggest__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef2f8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: #1f2430;
  transition: background 0.15s, border-color 0.15s, transform 0.15s;
}
.suggest__item:hover {
  background: #f5f9ff;
  border-color: #d6e6ff;
  transform: translateX(2px);
}
.suggest__icon {
  font-size: 16px;
}
.suggest__tag {
  font-size: 12px;
  color: #3b6ad6;
  background: #e9f0ff;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;
}
.suggest__text {
  flex: 1;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.suggest__arrow {
  color: #9aa3b2;
  font-size: 18px;
}

.list {
  flex: 1;
  overflow: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
}
.typing {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  padding: 4px 2px;
  background: transparent;
}
.typing__avatar {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}
.typing__text {
  line-height: 1;
}
.typing__dots {
  display: inline-flex;
  align-items: flex-end;
  gap: 3px;
  height: 12px;
  margin-left: 1px;
}
.typing__dots i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #9aa3b2;
  animation: dot-bounce 1.2s infinite ease-in-out;
}
.typing__dots i:nth-child(2) {
  animation-delay: 0.15s;
}
.typing__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.foot {
  text-align: center;
  font-size: 11px;
  color: #9aa3b2;
  padding: 6px 0 8px;
  background: #fff;
  border-top: 1px solid #f0f2f6;
}

/* agent-confirm-* / agent-delete-* 见 @/styles/agent-confirm-global.css */
</style>
